import { motion } from "framer-motion";
import { Mail, Instagram, ArrowUpRight, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const EMAIL = "sevawear0@gmail.com";
const INSTAGRAM_HANDLE = "@seva_wear";
const INSTAGRAM_URL = "https://instagram.com/seva_wear";

export default function CustomOrders() {
    const { t } = useLanguage();
    const CHECKLIST = t("custom.checklist");
    const CHANNELS = [
        {
            icon: Mail,
            title: "Email",
            label: t("custom.emailLabel"),
            value: EMAIL,
            href: `mailto:${EMAIL}?subject=Richiesta%20capo%20su%20ordinazione%20—%20Seva`,
            testid: "custom-order-email",
        },
        {
            icon: Instagram,
            title: "Instagram",
            label: t("custom.igLabel"),
            value: INSTAGRAM_HANDLE,
            href: INSTAGRAM_URL,
            testid: "custom-order-instagram",
        },
    ];
    return (
        <section id="su-commissione" className="py-24 md:py-32 px-5 md:px-10 border-t border-zinc-200" data-testid="custom-orders-section">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
                <div>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-xs tracking-[0.35em] uppercase text-zinc-500 mb-6"
                    >
                        {t("custom.eyebrow")}
                    </motion.p>

                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tight"
                    >
                        {t("custom.title")}
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2, duration: 0.7 }}
                        className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-xl"
                    >
                        {t("custom.body")}
                    </motion.p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.25, duration: 0.7 }}
                    className="bg-zinc-100 p-8 md:p-10"
                    data-testid="custom-order-checklist"
                >
                    <p className="text-xs tracking-[0.3em] uppercase font-semibold mb-6">
                        {t("custom.checklistTitle")}
                    </p>
                    <ul className="space-y-4">
                        {CHECKLIST.map((item, i) => (
                            <motion.li
                                key={i}
                                initial={{ opacity: 0, x: -16 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
                                className="flex items-start gap-3 text-sm md:text-base text-zinc-700"
                            >
                                <span className="mt-0.5 w-5 h-5 bg-black text-white flex items-center justify-center flex-shrink-0">
                                    <Check size={12} strokeWidth={3} />
                                </span>
                                {item}
                            </motion.li>
                        ))}
                    </ul>
                </motion.div>
            </div>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-[2px] bg-zinc-200 border border-zinc-200">
                {CHANNELS.map((ch, i) => (
                    <motion.a
                        key={ch.title}
                        href={ch.href}
                        target={ch.href.startsWith("http") ? "_blank" : undefined}
                        rel={ch.href.startsWith("http") ? "noopener noreferrer" : undefined}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.15 + i * 0.12, duration: 0.6 }}
                        className="group bg-white p-8 md:p-12 flex items-center justify-between transition-colors duration-300 hover:bg-black hover:text-white"
                        data-testid={ch.testid}
                    >
                        <div className="flex items-center gap-6">
                            <ch.icon size={32} strokeWidth={1.5} className="flex-shrink-0" />
                            <div>
                                <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 group-hover:text-zinc-300 transition-colors duration-300">
                                    {ch.label}
                                </p>
                                <p className="font-display text-lg md:text-2xl font-bold mt-1 break-all">
                                    {ch.value}
                                </p>
                            </div>
                        </div>
                        <ArrowUpRight
                            size={24}
                            className="flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                        />
                    </motion.a>
                ))}
            </div>

            <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="mt-6 text-xs text-zinc-400 tracking-wider"
            >
                {t("custom.note")}
            </motion.p>
        </section>
    );
}
